import { useElementSize } from '@mantine/hooks';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ClearIcon from '@mui/icons-material/Clear';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import {
  Box,
  Button,
  ButtonBase,
  FormControl,
  IconButton,
  MenuItem,
  Popover,
  Select,
  Stack,
  SxProps,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Theme,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import dayjs, { Dayjs } from 'dayjs';
import { strtotime } from 'locutus/php/datetime/strtotime';
import { memo, useEffect, useRef, useState } from 'react';
import { isDeepEqual } from 'remeda';
import { emdash, safeMaxDateTime, safeMinDateTime } from 'utility/constants';
import { getCommonPopoutSx, getSolidThemeGray } from 'utility/styles';
import { v4 as uuidv4 } from 'uuid';
import {
  dropdownInputAnchorOrigin,
  dropdownInputElevation,
  dropdownInputTransformOrigin,
  getCloseButtonSx,
  getDropdownInputThreshold,
  getFooterContainerSx,
  getFormControlSx,
  getHeaderLabelTypoSx,
  getInfoTextIconSx,
  getRemoveButtonSx,
  getSelectSx,
  getTooltipSx,
  InputChoice,
  maxPopoverHeightVh,
  maxPopoverWidthPxDateRange,
  tooltipPopperProps,
} from './dropdownConsts';
import { PossibleEvent } from './SelectDropdown/SelectDropdownInput';
import { SelectRenderValueElem } from './SelectDropdown/SelectRenderValueElem';
import { useTranslation } from 'react-i18next';

type DoubleStringArr = [string, string];

type TextDateRangeDropdownInputProps = {
  inputLabel?: string;
  value?: DoubleStringArr;
  updateValue: (event: PossibleEvent | any, value: DoubleStringArr) => void;
  formControlSx?: SxProps<Theme>;
  popoverPaperSx?: SxProps<Theme>;
  formControlVariant?: 'standard' | 'outlined';
  renderValueTooltip?: boolean;
  renderValueDisplay?: 'label' | 'chips';
  popoverMatchesFormControlWidth?: boolean;
  applyChangesOnPopoverClose?: boolean;
  inputPlaceholderText?: string;
  disabled?: boolean;
  hidden?: boolean;
  infoText?: string | JSX.Element;
  startDatePresets?: InputChoice[];
  endDatePresets?: InputChoice[];
  outputFormat?: 'raw' | 'YYYY-MM-DD';
  displayFormat?: 'raw' | 'pretty';
  useCalendarIconInSelect?: boolean;
};

function formatForDisplay(value: string, mode: 'raw' | 'pretty'): string {
  if (!value) return '';
  if (mode === 'raw') return value;
  const unix = strtotime(value);
  const d = unixToDayjs(unix);
  return d?.isValid() ? d.format('MMM D, YYYY') : value;
}

function formatOutgoing(value: string, mode: 'raw' | 'YYYY-MM-DD'): string {
  if (!value) return '';
  if (mode === 'raw') return value;
  const unix = strtotime(value);
  const parsed = unixToDayjs(unix);
  return parsed?.isValid() ? parsed.format('YYYY-MM-DD') : value;
}

function formatRangeForOutput(
  range: DoubleStringArr,
  mode: 'raw' | 'YYYY-MM-DD',
): DoubleStringArr {
  return [formatOutgoing(range[0], mode), formatOutgoing(range[1], mode)];
}

function cleanStringInput(value: string): string {
  if (/^\s/.test(value)) {
    return ''; // If the first character is a whitespace, return an empty string
  }

  return value
    .replace(/[\r\n\t\v\f]+/g, '') // replace with empty string (new lines, tabs, etc)
    .replace(/\s{2,}/g, ' '); // only allow single spaces
}

function checkValidInputDateString(date: string): string {
  if (date?.trim() === '') return ''; // empty input is ok
  // strtotime returns current unix time for empty str or whitespaces
  const checkDate = strtotime(date);
  return checkDate ? '' : 'blox.general.date_range.invalid_input';
}

function unixToDayjs(unix: number): Dayjs | null {
  if (!unix) return null;
  // Try to convert to Dayjs object
  const dayJsDate = dayjs.unix(unix);
  // Validate the dayjs date object
  if (!dayJsDate.isValid()) return null;
  return dayJsDate;
}

type DateValidationMessage = {
  start: string;
  end: string;
  range: string;
};

function checkValidDateRange(range: [string, string]): DateValidationMessage {
  const [startDate, endDate] = range;

  const startDateTrimmed = startDate?.trim() || '';
  const endDateTrimmed = endDate?.trim() || '';

  // Initialize the error messages - empty strings mean no error
  const errors: DateValidationMessage = {
    start: '',
    end: '',
    range: '',
  };

  // Check the start date
  if (startDateTrimmed) {
    const startError = checkValidInputDateString(startDateTrimmed);
    if (startError) {
      errors.start = startError;
    }
  }

  // Check the end date
  if (endDateTrimmed) {
    const endError = checkValidInputDateString(endDateTrimmed);
    if (endError) {
      errors.end = endError;
    }
  }

  // If both dates are valid, check the date range
  if (!errors.start && !errors.end && startDateTrimmed && endDateTrimmed) {
    const startUnix = strtotime(startDateTrimmed);
    const endUnix = strtotime(endDateTrimmed);

    const startDayjs = unixToDayjs(startUnix);
    const endDayjs = unixToDayjs(endUnix);

    const rangeInvalid =
      startDayjs?.isValid() && endDayjs?.isValid() && endDayjs.isBefore(startDayjs);
    if (rangeInvalid) {
      errors.range = 'blox.general.date_range.range_invalid';
    }
  }

  return errors;
}

export function getFormattedDateRangeLabel(
  range: DoubleStringArr,
  t?: (key: string, options?: Record<string, unknown>) => string,
): string {
  const [startDate, endDate] = range;
  const translate = t || ((key: string) => key);

  // Validate the date range and get the error messages
  const validationErrors = checkValidDateRange(range);

  // Return the validation error messages if there's an issue
  if (validationErrors.start || validationErrors.end || validationErrors.range) {
    const key = validationErrors.range || validationErrors.start || validationErrors.end;
    return translate(key);
  }

  // Both dates are valid, format the range label
  if (startDate && endDate) {
    return `${startDate} ${emdash} ${endDate}`;
  }

  // Handle label cases where only one date is provided
  if (startDate) {
    return translate('blox.general.date_range.start_prefix', { date: startDate });
  }

  if (endDate) {
    return translate('blox.general.date_range.end_prefix', { date: endDate });
  }

  // If all empty fallback to an empty string
  return '';
}

const DateFormatHelpTooltip = () => {
  const { t } = useTranslation();
  const examples = [
    {
      example: 'Feb 17, 2024',
      text: t('blox.general.date_range.examples.standard_textual'),
    },
    { example: '03/22/2019', text: t('blox.general.date_range.examples.american_mdy') },
    {
      example: 'January 1979',
      text: t('blox.general.date_range.examples.month_and_year'),
    },
    { example: 'today', text: t('blox.general.date_range.examples.start_of_today') },
    {
      example: 'next Monday',
      text: t('blox.general.date_range.examples.start_of_next_monday'),
    },
    { example: '-7 days', text: t('blox.general.date_range.examples.days_ago') },
    { example: '+2 weeks', text: t('blox.general.date_range.examples.weeks_from_now') },
  ];
  return (
    <Box>
      <Typography
        sx={{
          fontSize: 12,
        }}
      >
        {t('blox.general.date_range.help_text')}
      </Typography>
      <TableContainer>
        <Table
          aria-label="date field format examples"
          size="small"
          padding="none"
          sx={(theme) => ({
            '& td, & th': {
              color: 'white',
              py: 0.25,
              fontSize: 12,
              borderBottom: `1px solid ${theme.palette.divider}`,
            },
            '& thead th': {
              borderBottom: 'none',
            },
            '& tbody tr:last-of-type td': {
              borderBottom: 'none',
            },
          })}
        >
          <TableHead>
            <TableRow>
              <TableCell>{t('blox.general.date.label')}</TableCell>
              <TableCell>{t('blox.general.description')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {examples.map((row, index) => (
              <TableRow key={row.example}>
                <TableCell>{row.example}</TableCell>
                <TableCell>{row.text}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export const getStartDatePresetItems = (t: (key: string) => string): InputChoice[] => [
  { value: 'today', label: t('blox.general.date_range.presets.today') },
  { value: 'yesterday', label: t('blox.general.date_range.presets.yesterday') },
  { value: '-30 days', label: t('blox.general.date_range.presets.last_30_days') },
];

export const getEndDatePresetItems = (t: (key: string) => string): InputChoice[] => [
  { value: 'today', label: t('blox.general.date_range.presets.today') },
  { value: 'tomorrow', label: t('blox.general.date_range.presets.tomorrow') },
  { value: '+30 days', label: t('blox.general.date_range.presets.next_30_days') },
];

export const TextDateRangeDropdownInput = memo(
  ({
    inputLabel,
    value = ['', ''],
    inputPlaceholderText,
    updateValue,
    formControlSx,
    popoverPaperSx,
    formControlVariant = 'standard',
    renderValueTooltip,
    renderValueDisplay = 'label',
    infoText = <DateFormatHelpTooltip />,
    popoverMatchesFormControlWidth = false,
    applyChangesOnPopoverClose = false,
    disabled = false,
    hidden = false,
    startDatePresets,
    endDatePresets,
    outputFormat = 'raw',
    displayFormat = 'raw',
    useCalendarIconInSelect = false,
  }: TextDateRangeDropdownInputProps) => {
    // UI Vars / Hooks
    const theme = useTheme();
    const { t } = useTranslation();

    // Translated defaults for props that need t()
    const resolvedInputLabel = inputLabel || t('blox.general.date_range.select');
    const resolvedPlaceholderText = inputPlaceholderText || resolvedInputLabel;
    const resolvedStartDatePresets = startDatePresets || getStartDatePresetItems(t);
    const resolvedEndDatePresets = endDatePresets || getEndDatePresetItems(t);
    const [popoverOpen, setPopoverOpen] = useState(false);
    const popoverAnchorRef = useRef<HTMLElement | null>(null);
    const { ref: elSizeRef, width: formCtrlWidth } = useElementSize();

    // Setup consts / state for applyChangesOnPopoverClose prop
    const [stagedValues, setStagedValues] = useState<DoubleStringArr>(['', '']);

    // Sync values passed in to internal state...
    useEffect(() => {
      // Don't update local state if there's nothing to update
      if (isDeepEqual(value, stagedValues)) {
        return;
      }
      // Don't update local state if the popover is open
      // and we're in applyChangesOnPopoverClose mode
      if (popoverOpen && applyChangesOnPopoverClose) {
        return;
      }

      setStagedValues(value); // If we pass the guards, update the local state
    }, [stagedValues, popoverOpen, applyChangesOnPopoverClose, value]);

    // Determine the current values to use for the select - via passed value or local state copy?
    const currentValues = applyChangesOnPopoverClose ? stagedValues : value;

    // Const helpers
    const isDisabled = disabled;
    const isHidden = hidden;
    const hasStart = Boolean(currentValues[0]);
    const hasEnd = Boolean(currentValues[1]);

    // Error message strings if the input is invalid, else an empty string for valid.
    const validationResult = checkValidDateRange(currentValues);
    // Raw string values
    const startStringRaw = currentValues[0];
    const endStringRaw = currentValues[1];

    // Formatted display values
    const startDisplay = formatForDisplay(startStringRaw, displayFormat);
    const endDisplay = formatForDisplay(endStringRaw, displayFormat);

    // Stringified values for the select
    const jsonStringValueOrEmptyStr =
      JSON.stringify(currentValues) === '["",""]' ? '' : JSON.stringify(currentValues);

    // Get the string for the tooltip / chip field display (or displays invalid message)
    const formattedDateLabel = getFormattedDateRangeLabel([startDisplay, endDisplay], t);

    // Translation strings
    const strClose = t('blox.actions.close');
    const strRemove = t('blox.actions.remove');
    const strStartDate = t('blox.general.date.start_date');
    const strEndDate = t('blox.general.date.end_date');

    // Tooltip logic
    const showTooltip = renderValueTooltip || renderValueDisplay === 'label';
    const tooltipText = showTooltip ? formattedDateLabel : '';
    const tooltipTitle = !popoverOpen ? tooltipText : '';

    // Event handlers
    const handlePopoverOpen = () => {
      setPopoverOpen(true);
    };

    const handlePopoverClose = () => {
      const newValues: DoubleStringArr = [...currentValues];

      // Clear invalid values
      if (validationResult.start) {
        newValues[0] = '';
      }

      if (validationResult.start) {
        newValues[1] = '';
      }

      // If the range is invalid, clear both fields
      if (validationResult.range) {
        newValues[0] = '';
        newValues[1] = '';
      }

      const outgoing = formatRangeForOutput(newValues, outputFormat);
      updateValue(null, outgoing);
      setPopoverOpen(false);
    };

    // Proxy func to send the correct state update based on the popover close setting
    const applyChanges = (event: PossibleEvent | null, newValues: DoubleStringArr) => {
      // Update staged values or directly apply the changes
      if (applyChangesOnPopoverClose) {
        setStagedValues(newValues);
      } else {
        const outgoing = formatRangeForOutput(newValues, outputFormat);
        updateValue(event, outgoing);
      }
    };

    const handleDateChange = (newDate: string, field: 'start' | 'end') => {
      const newRange: DoubleStringArr = [...currentValues];

      if (field === 'start') {
        newRange[0] = newDate;
      }

      if (field === 'end') {
        newRange[1] = newDate;
      }

      applyChanges(null, newRange);
    };

    const handleClearAll = (event: PossibleEvent | null) => {
      const resetValues: DoubleStringArr = ['', ''];
      applyChanges(event, resetValues);
    };

    return (
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Tooltip
          title={tooltipTitle}
          disableInteractive={true}
          disableFocusListener={true}
          slotProps={{
            tooltip: {
              sx: {
                ...getTooltipSx(theme),
              },
            },
          }}
        >
          <FormControl
            size="small"
            variant={formControlVariant}
            ref={elSizeRef}
            disabled={isDisabled}
            hidden={isHidden}
            sx={{
              ...getFormControlSx(theme, popoverOpen),
              ...(typeof formControlSx === 'function'
                ? formControlSx(theme)
                : formControlSx),
              ...(isHidden ? { display: 'none' } : {}),
            }}
          >
            <Select
              ref={popoverAnchorRef}
              multiple={false}
              disabled={isDisabled}
              hidden={isHidden}
              data-testid="text-date-range-select-input"
              id={`${resolvedInputLabel}-daterange-select-input`}
              value={jsonStringValueOrEmptyStr}
              MenuProps={{ open: false }} // use custom popover
              onOpen={handlePopoverOpen}
              open={popoverOpen}
              IconComponent={useCalendarIconInSelect ? CalendarMonthIcon : undefined}
              sx={[
                getSelectSx,
                useCalendarIconInSelect && {
                  '& .MuiSelect-icon': {
                    fontSize: 20,
                  },
                  '& .MuiSelect-iconOpen': { transform: 'none' },
                },
              ]}
              displayEmpty={true}
              renderValue={() => (
                <SelectRenderValueElem
                  selectedChoices={
                    jsonStringValueOrEmptyStr
                      ? [{ value: jsonStringValueOrEmptyStr, label: formattedDateLabel }]
                      : []
                  }
                  placeholderText={resolvedPlaceholderText}
                  renderValueDisplay={renderValueDisplay}
                  isError={
                    Boolean(validationResult.start) ||
                    Boolean(validationResult.end) ||
                    Boolean(validationResult.range)
                  }
                />
              )}
            >
              {/**
               * To suppress the out-of-range value warning: add the empty
               * default & always have the current value as a choice.
               * for the date we'll put a json string of the current values in the value field.
               */}
              <MenuItem
                value=""
                hidden={true}
                style={{ display: 'none' }}
              />
              <MenuItem
                value={jsonStringValueOrEmptyStr}
                hidden={true}
                style={{ display: 'none' }}
              />
            </Select>

            <Popover
              anchorEl={popoverAnchorRef.current}
              open={popoverOpen && !isDisabled}
              hidden={isHidden}
              onClose={handlePopoverClose}
              anchorOrigin={dropdownInputAnchorOrigin}
              transformOrigin={dropdownInputTransformOrigin}
              elevation={dropdownInputElevation}
              marginThreshold={getDropdownInputThreshold(theme)}
              slotProps={{
                paper: {
                  sx: {
                    display: 'grid',
                    gridTemplateRows: 'auto auto 1fr',
                    gridTemplateAreas: `'selectheader' 'selectmainarea' 'selectfooter'`,
                    container: 'selectgrid / normal',
                    overflow: 'hidden',
                    width: popoverMatchesFormControlWidth
                      ? formCtrlWidth
                      : maxPopoverWidthPxDateRange,
                    maxHeight: maxPopoverHeightVh,
                    gap: 0.5,
                    bgcolor: getSolidThemeGray(theme),
                    ...getCommonPopoutSx(theme),
                    ...(typeof popoverPaperSx === 'function'
                      ? popoverPaperSx(theme)
                      : popoverPaperSx),
                  },
                },
              }}
            >
              <Stack
                direction="row"
                sx={{
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gridArea: 'selectheader',
                  borderBottom: `1px solid ${theme.palette.action.selected}`,
                  p: 1,
                }}
              >
                <Tooltip
                  title={infoText}
                  arrow={false}
                  disableInteractive={true}
                  enterDelay={600}
                  enterNextDelay={300}
                  placement="bottom-start"
                  slotProps={{
                    popper: tooltipPopperProps,
                  }}
                >
                  <Typography
                    sx={getHeaderLabelTypoSx}
                    aria-label={`${resolvedInputLabel}${infoText && ` - ${infoText}`}`}
                  >
                    {resolvedInputLabel}
                    {infoText ? <InfoOutlinedIcon sx={getInfoTextIconSx} /> : null}
                  </Typography>
                </Tooltip>
                <Button
                  data-testid="text-date-range-clear-btn"
                  variant="text"
                  size="small"
                  color="error"
                  disabled={!hasStart && !hasEnd} // disable if both fields are empty
                  startIcon={<RemoveCircleOutlineIcon />}
                  sx={getRemoveButtonSx}
                  onClick={(event) => handleClearAll(event)}
                >
                  {strRemove}
                </Button>
              </Stack>

              <Stack
                direction="column"
                sx={{
                  gridArea: 'selectmainarea',
                  px: 1,
                  gap: 1,
                }}
              >
                {/* --- Start Date Field --- */}
                <TextFieldDatePicker
                  dataTestId="text-date-range-start-input"
                  label={strStartDate}
                  value={startDisplay}
                  onChange={(newValue) => handleDateChange(newValue, 'start')}
                  error={
                    Boolean(validationResult.start) || Boolean(validationResult.range)
                  }
                  helperText={
                    validationResult.start ? t(validationResult.start) : undefined
                  }
                  disabled={disabled}
                  presetItems={resolvedStartDatePresets}
                  minDate={safeMinDateTime}
                  maxDate={safeMaxDateTime}
                />

                {/* --- End Date Field --- */}
                <TextFieldDatePicker
                  dataTestId="text-date-range-end-input"
                  label={strEndDate}
                  value={endDisplay}
                  onChange={(newValue) => handleDateChange(newValue, 'end')}
                  error={Boolean(validationResult.end) || Boolean(validationResult.range)}
                  helperText={validationResult.end ? t(validationResult.end) : undefined}
                  disabled={disabled}
                  presetItems={resolvedEndDatePresets}
                  minDate={safeMinDateTime}
                  maxDate={safeMaxDateTime}
                />

                {/* --- range error display message --- */}
                {validationResult.range && (
                  <Typography
                    variant="caption"
                    color="error"
                    sx={{
                      py: 0.5,
                    }}
                  >
                    {t(validationResult.range)}
                  </Typography>
                )}
              </Stack>

              <Stack
                sx={{
                  gridArea: 'selectfooter',
                  display: 'grid',
                  gridTemplateColumns: '1fr auto',
                  gridTemplateAreas: `"listinfo closebtn"`,
                  alignItems: 'center',
                  ...getFooterContainerSx(theme, true),
                }}
              >
                {/* Spacer to keep the close button location consistent with other advanced filters */}
                <Box sx={{ gridArea: 'listinfo' }} />

                <ButtonBase
                  data-testid="text-date-range-close-btn"
                  onClick={handlePopoverClose}
                  sx={{
                    gridArea: 'closebtn',
                    ...getCloseButtonSx(theme),
                  }}
                >
                  {strClose}
                </ButtonBase>
              </Stack>
            </Popover>
          </FormControl>
        </Tooltip>
      </LocalizationProvider>
    );
  },
);

TextDateRangeDropdownInput.displayName = 'TextDateRangeDropdownInput';

export interface TextFieldDatePickerProps {
  /**
   * value of the input internally is parsed by strtotime -> unix ts -> dayjs obj | null (if invalid)
   * note also backend apis should be applying time rounding e.g if sending "+30 days", we
   * do not need to worry about normalizing time like "+30 days midnight".
   */
  value: string;
  label: string;
  onChange: (newValue: string) => void;
  error: boolean;
  helperText?: string;
  disabled?: boolean;
  presetItems?: InputChoice[];
  minDate?: Dayjs;
  maxDate?: Dayjs;
  dataTestId?: string;
}

export const TextFieldDatePicker = memo(
  ({
    label,
    value,
    onChange,
    error,
    helperText,
    disabled = false,
    presetItems = [],
    minDate,
    maxDate,
    dataTestId,
  }: TextFieldDatePickerProps) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const boxid = `${label?.toLowerCase()}-input-group`;
    const [key, setKey] = useState<string>(uuidv4());
    const [pickerAnchorEl, setPickerAnchorEl] = useState<HTMLElement | null>(null);
    const textFieldRef = useRef<HTMLInputElement | null>(null);

    // Convert the value to a Dayjs object for the calendar or null if unparsable
    const dayjsValue: Dayjs | null = value ? unixToDayjs(strtotime(value)) : null;

    // Handle opening/closing the date picker popover
    const handlePickerPopoverState = (event: React.MouseEvent<HTMLElement> | null) => {
      setPickerAnchorEl(event ? event.currentTarget : null);
    };

    // Handle date selection from the calendar - format to date string
    const handleDateChangeInternal = (newDate: Dayjs | null) => {
      if (newDate) {
        const formattedDate = newDate.format('MMM D, YYYY');
        onChange(formattedDate);
      } else {
        onChange('');
      }
      handlePickerPopoverState(null);
    };

    // Handle clearing the field via the clear button
    const handleClearFieldInternal = () => {
      setKey(uuidv4()); // have to force re-render to clear internal MUI states
      onChange(''); // clear the field
      setTimeout(() => textFieldRef.current?.focus(), 0); // restore focus to the text field
    };

    return (
      <Box id={boxid}>
        <TextField
          data-testid={dataTestId}
          key={key}
          inputRef={textFieldRef}
          label={label}
          size="small"
          variant="outlined"
          autoComplete="off"
          value={value}
          onChange={(e) => {
            const cleaned = cleanStringInput(e.target.value); // for strtotime format
            onChange(cleaned);
          }}
          disabled={disabled}
          error={error}
          helperText={helperText}
          fullWidth
          sx={{
            '& .MuiInputBase-root': {
              pr: 1,
              bgcolor: theme.palette.background.paper,
            },
          }}
          slotProps={{
            input: {
              endAdornment: (
                <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1 }}>
                  <Tooltip
                    title={t('blox.general.date_range.clear_input')}
                    disableInteractive
                  >
                    <span>
                      <IconButton
                        onClick={handleClearFieldInternal}
                        disabled={disabled}
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip
                    title={t('blox.general.select_date')}
                    disableInteractive
                  >
                    <span>
                      <IconButton
                        onClick={(event) => handlePickerPopoverState(event)}
                        disabled={disabled}
                      >
                        <CalendarMonthIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              ),
            },
          }}
        />
        {/* --- Preset Buttons --- */}
        {presetItems.length ? (
          <Box
            data-testid={`${dataTestId}-preset-btns-container`}
            sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: 1 }}
          >
            {presetItems.map((chip, index) => (
              <Button
                data-testid={`${dataTestId}-${index}-preset-btn`}
                key={chip.value}
                size="small"
                disableElevation
                onClick={() => onChange(chip.value)}
                variant="outlined"
                color={chip.value === value ? 'primary' : 'info'}
                sx={{
                  fontSize: 12,
                  fontWeight: 400,
                  py: 0.25,
                  px: 0.5,
                }}
              >
                {chip.label}
              </Button>
            ))}
          </Box>
        ) : null}
        {/* --- Date Picker Popover --- */}
        <Popover
          key={`${key}-popover`}
          anchorEl={pickerAnchorEl}
          open={Boolean(pickerAnchorEl)}
          onClose={() => handlePickerPopoverState(null)}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
        >
          <DateCalendar
            value={dayjsValue}
            disabled={disabled}
            maxDate={maxDate}
            minDate={minDate}
            reduceAnimations={true}
            onChange={(newDate: Dayjs | null, selectionState) => {
              // Send up a string e.g. "Feb 17, 2022" for the backend
              // API to parse and handle the site timezone as needed
              // Close popover when MUI DateCalendar sends finish state
              if (newDate && selectionState === 'finish') {
                handleDateChangeInternal(newDate);
              }
            }}
          />
        </Popover>
      </Box>
    );
  },
);
TextFieldDatePicker.displayName = 'TextFieldDatePicker';
